import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchYdelserFromCMS, fetchGallerierFromCMS, FALLBACK_YDELSER, FALLBACK_GALLERI } from '../lib/cms';
import LightboxModal from '../components/LightboxModal';

export default function ServiceDetail() {
  const { slug } = useParams();

  const [ydelser, setYdelser] = useState(FALLBACK_YDELSER);
  const [gallerier, setGallerier] = useState(FALLBACK_GALLERI);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  // Quick form state
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '', website_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    async function loadData() {
      const cmsYdelser = await fetchYdelserFromCMS();
      if (cmsYdelser) setYdelser(cmsYdelser);

      const cmsGallerier = await fetchGallerierFromCMS();
      if (cmsGallerier) setGallerier(cmsGallerier);
    }
    loadData();
  }, [slug]);

  const service = ydelser.find((y) => y.service_slug === slug) || FALLBACK_YDELSER.find((y) => y.service_slug === slug);

  if (!service) {
    return (
      <div className="py-24 text-center bg-[#dce2d7] text-[#1f241d]">
        <h1 className="font-heading text-3xl font-bold text-[#1f241d]">Ydelsen blev ikke fundet</h1>
        <p className="text-gray-600 mt-2">Den efterspurgte ydelsesside findes desværre ikke.</p>
        <Link to="/ydelser" className="mt-6 inline-block bg-[#3d5229] text-white px-6 py-2.5 rounded-none font-semibold">
          &larr; Se alle ydelser
        </Link>
      </div>
    );
  }

  // Find related gallery based on gallery_slug
  const relatedGallery = gallerier.find((g) => g.gallery_slug === service.gallery_slug || g.gallery_slug === service.service_slug);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      setServerError('Udfyld venligst alle påkrævede felter.');
      return;
    }

    setSubmitting(true);
    setServerError('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          service: service.title,
          website_url: formData.website_url // Honeypot field
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        setServerError(data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setServerError('Kunne ikke oprette forbindelse. Ring til Tlf. 22 23 10 10.');
    } finally {
      setSubmitting(false);
    }
  };

  const otherServices = ydelser.filter((y) => y.service_slug !== service.service_slug);

  const heroImage = (service.image && service.image[0] && (service.image[0].includes('/imgi-') || service.image[0].includes('/imgi_'))) 
    ? service.image[0]
    : "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-27-prentreprise-1_b16be56f.jpeg";

  // Content splitting and parsing logic for engaging interactive display
  const rawContent = service.content || '';

  // Parse HTML into sub-sections if present (splits at <h3> or <h2>)
  const parseContentSections = (html) => {
    if (!html) return { intro: '', sections: [] };

    const headingRegex = /<(h2|h3)[^>]*>(.*?)<\/\1>/gi;
    const matches = [...html.matchAll(headingRegex)];

    if (matches.length === 0) {
      return { intro: html, sections: [] };
    }

    const firstMatchIndex = matches[0].index;
    const intro = html.substring(0, firstMatchIndex).trim();

    const sections = matches.map((match, idx) => {
      const title = match[2].replace(/<[^>]+>/g, '').trim();
      const startPos = match.index + match[0].length;
      const endPos = matches[idx + 1] ? matches[idx + 1].index : html.length;
      const body = html.substring(startPos, endPos).trim();

      let icon = 'fa-circle-info';
      const lowerTitle = title.toLowerCase();
      if (lowerTitle.includes('pris') || lowerTitle.includes('koster')) icon = 'fa-calculator';
      else if (lowerTitle.includes('indkørsel') || lowerTitle.includes('vej')) icon = 'fa-car';
      else if (lowerTitle.includes('kvalitet') || lowerTitle.includes('sikker')) icon = 'fa-award';
      else if (lowerTitle.includes('kontakt') || lowerTitle.includes('tilbud')) icon = 'fa-paper-plane';
      else if (lowerTitle.includes('hvad er') || lowerTitle.includes('om')) icon = 'fa-circle-question';

      return { title, body, icon };
    });

    return { intro, sections };
  };

  const { intro: parsedIntro, sections: parsedSections } = parseContentSections(rawContent);

  // State for active accordion section (open first one by default if sections exist)
  const [openAccordion, setOpenAccordion] = useState(0);

  // Assemble candidate list of distinct images for this service using user uploaded project photos
  const userUploadsPool = [
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-23-motorboer-paa-el.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-prentreprise-2.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-male-worker-screeding-floor-construction-site-651396-2362_54c98602.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-29-sandpude-1_a8edb6bd.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-28-sandpude-2_e7ae8ae6.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-25-sandpude-2_1294176c.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-udgravning-til-indkoersel-2_25550a64.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-25-udgravning-til-baalsted-3_e738205b.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-udgravning-til-bed-1_0c990ea4.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-27-udgravning-til-baalsted-2_1e2af2c3.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-28-sandpude-2_e7ae8ae6.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-29-sandpude-1_a8edb6bd.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-jordvarme_dbefc4a0.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-27-prentreprise-1_b16be56f.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-udgravning-til-indkoersel-1_8b22c2e3.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-25-sandpude-2_d371b171.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-28-udgravning-til-baalsted-2_c931bacc.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-29-prentreprise-1-1022x1024.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-28-udgravning-til-indkoersel-2-768x1024.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-27-jordvarme-1024x683.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-prentreprise-4.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-25-prentreprise-2.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-udgravning-til-faskiner-1.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-26-belaegning-5.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-25-belaegning-4.jpeg",
    "https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-24-belaegning-6.jpeg"
  ];

  const rawGalleryFromService = [
    ...(service.image || []),
    ...(relatedGallery?.images || [])
  ];

  // Filter images so only user upload photos are kept (urls containing 'imgi' or userUploadsPool)
  const isUserPhoto = (url) => typeof url === 'string' && (url.includes('/imgi-') || url.includes('/imgi_'));

  const filteredServiceImages = rawGalleryFromService.filter(isUserPhoto);

  const candidateImages = [
    heroImage,
    ...filteredServiceImages,
    ...userUploadsPool
  ].filter(isUserPhoto);

  const serviceFiveImages = Array.from(new Set(candidateImages.filter(Boolean))).slice(0, 10);

  const activeGalleryImages = (relatedGallery?.images && relatedGallery.images.some(isUserPhoto))
    ? relatedGallery.images.filter(isUserPhoto)
    : serviceFiveImages;

  const openLightbox = (imgIdx = 0) => {
    setSelectedGallery({
      title: service.title,
      description: service.summary,
      images: activeGalleryImages
    });
    setCurrentImgIndex(imgIdx);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-[#dce2d7] text-[#1f241d]">
      <Helmet>
        <title>{service.meta_title || `${service.title} - PR Entreprise & Anlæg ApS`}</title>
        <meta
          name="description"
          content={service.meta_description || `${service.title} udført af PR Entreprise & Anlæg ApS i Ishøj og omegn.`}
        />
        <meta property="og:title" content={service.meta_title || `${service.title} - PR Entreprise & Anlæg ApS`} />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#141f14] text-white py-14 lg:py-20 border-b border-[#233323] overflow-hidden">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141f14] via-[#141f14]/90 to-transparent pointer-events-none" />
        <div className="relative max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4 animate-fade-in-up">
              <div className="flex items-center gap-2 text-xs text-[#9ecc49]">
                <Link to="/" className="hover:underline">Forside</Link>
                <span>/</span>
                <Link to="/ydelser" className="hover:underline">Vi tilbyder</Link>
                <span>/</span>
                <span className="text-white font-medium">{service.title}</span>
              </div>

            <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {service.title}
            </h1>

            {service.summary && (
              <p className="text-[#e8eee6] text-base sm:text-xl leading-relaxed">
                {service.summary}
              </p>
            )}

            {/* Highlights bullet points */}
            <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-[#dce2d7]">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-[#9ecc49]" aria-hidden="true" />
                <span>Autoriseret kloakmester</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-[#9ecc49]" aria-hidden="true" />
                <span>20+ års erfaring</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-[#9ecc49]" aria-hidden="true" />
                <span>Opstart og levering til tiden</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-[#9ecc49]" aria-hidden="true" />
                <span>Vi guider dig fra start til slut</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#dce2d7] text-[#1f241d]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            <div className="lg:col-span-8 space-y-8">
              {/* Alternating Content Blocks (Zig-Zag Rhythm) */}
              <div className="space-y-8">

                {/* Block 2: PHOTO - TEXT (Process / Workflow) */}

                {/* Block 2: PHOTO - TEXT (Process / Workflow) */}
                <div className="bg-[#141f14] text-white p-6 sm:p-8 rounded-none border border-[#233323] shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
                    
                    {/* Photo 2 (Left) */}
                    {serviceFiveImages[1] && (
                      <div className="md:col-span-5 flex flex-col order-2 md:order-1">
                        <div
                          onClick={() => openLightbox(1)}
                          className="bg-[#1f241d] p-1 border border-[#3d5229]/40 overflow-hidden shadow-sm group relative h-full min-h-[260px] cursor-pointer"
                        >
                          <img
                            src={serviceFiveImages[1]}
                            alt={`${service.title} proces`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="bg-[#141f14]/90 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 border border-[#9ecc49]">
                              <i className="fa-solid fa-magnifying-glass-plus text-[#9ecc49]" aria-hidden="true" />
                              <span>Billede 2</span>
                            </span>
                          </div>
                          <div className="absolute bottom-2 left-2 bg-[#3d5229] text-white px-2 py-0.5 text-xs font-bold">
                            Autoriseret Udførelse
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Text (Right) */}
                    <div className={`space-y-4 flex flex-col justify-center order-1 md:order-2 ${serviceFiveImages[1] ? 'md:col-span-7' : 'md:col-span-12'}`}>
                      <div className="flex flex-col items-start gap-3 border-b border-[#233323] pb-3">
                        <div>
                          <span className="text-xs text-[#9ecc49] font-bold uppercase tracking-widest block">Processen</span>
                          <h3 className="font-heading text-xl sm:text-2xl font-bold text-white">
                            Sådan forløber dit projekt
                          </h3>
                        </div>
                        <a
                          href="tel:22231010"
                          className="inline-flex items-center gap-2 text-xs font-bold text-[#1f241d] bg-[#9ecc49] hover:bg-[#8ebb3c] px-3.5 py-2 transition-colors self-start sm:self-auto"
                        >
                          <i className="fa-solid fa-phone" aria-hidden="true" />
                          <span>Ring 22 23 10 10</span>
                        </a>
                      </div>

                      <div className="grid grid-cols-1 gap-3 text-xs">
                        <div className="bg-[#1f241d] p-3.5 border border-[#233323] space-y-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-sm text-white flex items-center gap-2">
                              <i className="fa-solid fa-comments text-[#9ecc49]" aria-hidden="true" />
                              Rådgivning & Tilbud
                            </h4>
                            <span className="text-[10px] font-bold text-[#9ecc49] bg-[#3d5229] px-2 py-0.5">Trin 1</span>
                          </div>
                          <p className="text-gray-300 leading-relaxed">
                            Vi drøfter dine ønsker, besigtiger opgaven og giver et uforpligtende fast tilbud.
                          </p>
                        </div>

                        <div className="bg-[#1f241d] p-3.5 border border-[#233323] space-y-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-sm text-white flex items-center gap-2">
                              <i className="fa-solid fa-trowel-bricks text-[#9ecc49]" aria-hidden="true" />
                              Grundig Udførelse
                            </h4>
                            <span className="text-[10px] font-bold text-[#9ecc49] bg-[#3d5229] px-2 py-0.5">Trin 2</span>
                          </div>
                          <p className="text-gray-300 leading-relaxed">
                            Korrekt bundopbygning, udgravning og præcis lægning/installation efter alle forskrifter.
                          </p>
                        </div>

                        <div className="bg-[#1f241d] p-3.5 border border-[#233323] space-y-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-sm text-white flex items-center gap-2">
                              <i className="fa-solid fa-circle-check text-[#9ecc49]" aria-hidden="true" />
                              Kvalitetsaflevering
                            </h4>
                            <span className="text-[10px] font-bold text-[#9ecc49] bg-[#3d5229] px-2 py-0.5">Trin 3</span>
                          </div>
                          <p className="text-gray-300 leading-relaxed">
                            Slutaflevering, oprydning og gennemgang af det færdige smukke resultat.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Sub-sections alternating blocks (if parsedSections exist) */}
                {parsedSections.map((sec, idx) => {
                  const isEven = idx % 2 === 0; // even: Text Left, Photo Right; odd: Photo Left, Text Right
                  const assocImg = serviceFiveImages[2 + idx] || serviceFiveImages[idx % serviceFiveImages.length];

                  return (
                    <div key={idx} className="bg-white p-6 sm:p-8 rounded-none border border-[#cbd3c5] shadow-sm text-[#1f241d]">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
                        
                        {/* Text Column */}
                        <div className={`space-y-4 flex flex-col justify-center ${isEven ? 'order-1 md:col-span-7' : 'order-1 md:order-2 md:col-span-7'}`}>
                          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#3d5229] bg-[#dce2d7] px-3 py-1 rounded-none w-max">
                            <i className={`fa-solid ${sec.icon} text-[#3d5229]`} aria-hidden="true" />
                            <span>Informativ Vejledning</span>
                          </div>

                          <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#1f241d]">
                            {sec.title}
                          </h3>

                          <div
                            className="prose max-w-none text-sm sm:text-base leading-relaxed text-[#1f241d]"
                            dangerouslySetInnerHTML={{ __html: sec.body }}
                          />

                          <div className="pt-2">
                            <a
                              href="tel:22231010"
                              className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#3d5229] hover:bg-[#2e3f1e] px-4 py-2.5 transition-colors"
                            >
                              <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                              <span>Spørg os om {sec.title.toLowerCase()}</span>
                            </a>
                          </div>
                        </div>

                        {/* Photo Column */}
                        {assocImg && (
                          <div className={`flex flex-col ${isEven ? 'order-2 md:col-span-5' : 'order-2 md:order-1 md:col-span-5'}`}>
                            <div
                              onClick={() => openLightbox((2 + idx) % activeGalleryImages.length)}
                              className="bg-[#dce2d7] p-1 border border-[#cbd3c5] overflow-hidden shadow-sm group relative h-full min-h-[260px] cursor-pointer"
                            >
                              <img
                                src={assocImg}
                                alt={sec.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="bg-[#141f14]/90 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 border border-[#9ecc49]">
                                  <i className="fa-solid fa-magnifying-glass-plus text-[#9ecc49]" aria-hidden="true" />
                                  <span>Billede {3 + idx}</span>
                                </span>
                              </div>
                              <div className="absolute bottom-2 left-2 bg-[#141f14]/85 text-[#9ecc49] text-[10px] font-bold px-2 py-0.5 border border-[#3d5229]/40 backdrop-blur-xs">
                                Billede {3 + idx} - PR Entreprise
                              </div>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  );
                })}

              </div>

              {/* Related Dynamic CMS Gallery */}
              {relatedGallery && relatedGallery.images && relatedGallery.images.length > 0 && (
                <div className="bg-white p-8 rounded-none border border-[#cbd3c5] shadow-sm space-y-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-[#cbd3c5] pb-4">
                    <div>
                      <span className="text-xs text-[#3d5229] font-bold uppercase tracking-widest">CMS Galleri</span>
                      <h3 className="font-heading text-2xl font-bold text-[#1f241d]">
                        {relatedGallery.title}
                      </h3>
                    </div>
                    <span className="text-xs text-gray-600 font-semibold bg-[#dce2d7] px-3 py-1 rounded-none self-start sm:self-auto">
                      Klik for stor visning
                    </span>
                  </div>

                  {relatedGallery.description && (
                    <p className="text-sm text-[#4e564a]">{relatedGallery.description}</p>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {relatedGallery.images.map((imgUrl, idx) => (
                      <div
                        key={idx}
                        onClick={() => openLightbox(idx)}
                        className="group relative h-40 rounded-none overflow-hidden cursor-pointer border border-[#cbd3c5] bg-gray-900"
                      >
                        <img
                          src={imgUrl}
                          alt={`${relatedGallery.title} billede ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <i className="fa-solid fa-magnifying-glass-plus text-2xl text-[#9ecc49]" aria-hidden="true" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA card */}
              <div className="bg-[#141f14] text-white p-8 rounded-none border border-[#233323] shadow-lg space-y-4">
                <h3 className="font-heading text-2xl font-bold text-[#9ecc49]">
                  Skal vi hjælpe dig med {service.title.toLowerCase()}?
                </h3>
                <p className="text-white text-sm sm:text-base leading-relaxed">
                  Vi rådgiver dig gerne om materialevalg, bundopbygning og priser. Kontakt os for et uforpligtende tilbud i dag.
                </p>
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <a
                    href="tel:22231010"
                    className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white px-5 py-3 rounded-none font-semibold text-sm border border-[#9ecc49]/40 flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
                    <span className="whitespace-nowrap">Tlf. 22 23 10 10</span>
                  </a>
                  <a
                    href="mailto:info@prentreprise.dk"
                    className="bg-transparent hover:bg-white/10 text-white px-5 py-3 rounded-none font-semibold text-sm border border-white/20 flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-[#9ecc49]" aria-hidden="true" />
                    <span className="whitespace-nowrap">info@prentreprise.dk</span>
                  </a>
                </div>
              </div>

            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Quote / Contact Form Widget */}
              <div className="bg-white p-6 rounded-none border border-[#cbd3c5] shadow-sm space-y-4">
                <div className="border-b border-[#cbd3c5] pb-3">
                  <span className="text-xs text-[#3d5229] font-bold uppercase tracking-wider block">Uforpligtende tilbud</span>
                  <h3 className="font-heading font-bold text-xl text-[#1f241d]">
                    Få et godt tilbud
                  </h3>
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <p className="flex items-center gap-2">
                      <i className="fa-solid fa-phone text-[#3d5229]" aria-hidden="true" />
                      <a href="tel:22231010" className="font-bold text-[#1f241d] hover:underline whitespace-nowrap">Tlf. 22 23 10 10</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="fa-solid fa-envelope text-[#3d5229]" aria-hidden="true" />
                      <a href="mailto:info@prentreprise.dk" className="font-bold text-[#1f241d] hover:underline whitespace-nowrap">info@prentreprise.dk</a>
                    </p>
                  </div>
                </div>

                {submitted ? (
                  <div className="bg-[#3d5229]/10 border border-[#3d5229] p-4 text-[#1f241d] text-sm space-y-2">
                    <div className="font-bold text-[#3d5229] flex items-center gap-2">
                      <i className="fa-solid fa-check-circle" aria-hidden="true" />
                      Mange tak for din henvendelse!
                    </div>
                    <p className="text-xs text-gray-600">Vi vender tilbage til dig hurtigst muligt med et uforpligtende tilbud.</p>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-3.5 text-xs">
                    {/* Honeypot field */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <input
                        type="text"
                        name="website_url"
                        tabIndex={-1}
                        value={formData.website_url}
                        onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                        autoComplete="off"
                      />
                    </div>

                    {serverError && (
                      <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs">
                        {serverError}
                      </div>
                    )}

                    <div>
                      <label className="block font-bold text-[#1f241d] mb-1">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Dit fulde navn"
                        className="w-full px-3 py-2 border border-[#cbd3c5] focus:outline-none focus:border-[#3d5229] text-sm"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-[#1f241d] mb-1">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Tlf. nummer"
                        className="w-full px-3 py-2 border border-[#cbd3c5] focus:outline-none focus:border-[#3d5229] text-sm"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-[#1f241d] mb-1">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Din e-mailadresse"
                        className="w-full px-3 py-2 border border-[#cbd3c5] focus:outline-none focus:border-[#3d5229] text-sm"
                      />
                    </div>

                    <div>
                      <label className="block font-bold text-[#1f241d] mb-1">
                        Besked <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Beskriv kort din opgave..."
                        className="w-full px-3 py-2 border border-[#cbd3c5] focus:outline-none focus:border-[#3d5229] text-sm resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#3d5229] hover:bg-[#2e3f1e] text-white py-2.5 px-4 font-heading font-bold text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Sender...' : 'Send henvendelse'}
                    </button>
                  </form>
                )}
              </div>



              {/* Company Info Box */}
              <div className="bg-white p-6 rounded-none border border-[#cbd3c5] space-y-3.5 text-xs text-[#1f241d]">
                <h4 className="font-heading font-bold text-sm text-[#1f241d] uppercase tracking-wider mb-2 border-b border-[#cbd3c5] pb-2">
                  PR Entreprise & Anlæg ApS
                </h4>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-location-dot text-[#3d5229] w-4 text-center shrink-0" aria-hidden="true" />
                  <span>Ishøj Bygade 90, 2635 Ishøj</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-id-card text-[#3d5229] w-4 text-center shrink-0" aria-hidden="true" />
                  <span>CVR: 43852507</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-phone text-[#3d5229] w-4 text-center shrink-0" aria-hidden="true" />
                  <a href="tel:22231010" className="font-bold text-[#1f241d] hover:underline whitespace-nowrap">
                    Tlf. 22 23 10 10
                  </a>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-envelope text-[#3d5229] w-4 text-center shrink-0" aria-hidden="true" />
                  <a href="mailto:info@prentreprise.dk" className="font-bold text-[#1f241d] hover:underline whitespace-nowrap">
                    info@prentreprise.dk
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Component */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={selectedGallery?.images || activeGalleryImages}
        currentIndex={currentImgIndex}
        setCurrentIndex={setCurrentImgIndex}
        caption={selectedGallery?.caption || selectedGallery?.description}
        title={selectedGallery?.title || service.title}
      />
    </div>
  );
}
